<template>
  <div v-if="ready">
    <!-- Niets om naartoe te wijzigen: dan geen wizard met alleen het eigen,
         uitgeschakelde tijdslot, maar zeggen wat wel kan (ClickUp 1240ccedj0x).
         Opgebouwd als de andere meldingen: kaart, sluitkruis, titel en tekst
         gecentreerd. Dezelfde melding als de Afmeldtermijn voorbij is: dan mag
         de deelnemer zijn eigen boeking niet meer wijzigen (ClickUp 1240ccedn8d). -->
    <v-dialog
      v-if="heldByCancellationWindow || !changeable"
      v-model="unavailable_dialog"
      width="640"
      content-class="card"
    >
      <v-btn
        icon
        variant="text"
        density="compact"
        class="dialog-close-btn"
        :aria-label="$t('components.changeActivityDialog.unavailable_close')"
        @click="unavailable_dialog = false"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <div class="text-center">
        <h2 class="mb-4">
          {{ $t("components.changeActivityDialog.unavailable_title") }}
        </h2>
        <span class="description-text">
          {{
            $t(
              heldByCancellationWindow
                ? "components.changeActivityDialog.period_ended_text"
                : forOther
                ? "components.changeActivityDialog.unavailable_text_other"
                : "components.changeActivityDialog.unavailable_text",
              {
                date: enrollDate,
                start: enroll.start,
                period: cancellationPeriodLabel,
              }
            )
          }}
        </span>
      </div>
      <div class="d-flex justify-center mt-8">
        <v-btn
          variant="flat"
          class="btn text-white"
          color="var(--company-primary)"
          :block="is_mobile"
          @click="unavailable_dialog = false"
        >
          {{ $t("components.changeActivityDialog.unavailable_close") }}
        </v-btn>
      </div>
    </v-dialog>
    <change-single-time-dialog
      @update-enroll="$emit('update-enroll', $event)"
      :activity="activity"
      :enroll="enroll"
      :page="page"
      v-else-if="enroll.type == 'eenopeen'"
      v-model="single_time_dialog"
      @close-dialog="single_time_dialog = false"
    />
    <change-group-time-dialog
      @update-enroll="$emit('update-enroll', $event)"
      :activity="activity"
      :enroll="enroll"
      v-else-if="enroll.type == 'groepsverband'"
      :page="page"
      v-model="group_time_dialog"
      @close-dialog="group_time_dialog = false"
    />
  </div>
</template>
<script>
import { useMainStore } from "../../stores/store";
import ChangeSingleTimeDialog from "./changeSingleTimeDialog.vue";
import ChangeGroupTimeDialog from "./changeGroupTimeDialog.vue";
import api from "../../services/api";
import config from "../../config";
import functions from "@/util/functions";
import isMobile from "@/mixins/isMobile";
import { hasSlotToChangeTo } from "@/util/activityChangeable";
import { isPastCancellationWindow } from "@/util/cancellationWindow";
import { toAmsterdamCalendarDate } from "@/util/DateUtil";
export default {
  mixins: [isMobile],
  components: { ChangeSingleTimeDialog, ChangeGroupTimeDialog },

  props: {
    /**
     * De boeking is van een ander (werkgever of specialist in het
     * aanmeldingenoverzicht); alleen de tekst van "Wijzigen niet mogelijk"
     * hangt ervan af.
     */
    forOther: { type: Boolean, default: false },
    /**
     * De boeking wacht op een Verplaatsingsbevestiging. De deelnemer koos dit
     * moment niet zelf, dus de Afmeldtermijn houdt hem er niet aan (ADR-0074).
     */
    awaitingMoveConfirmation: { type: Boolean, default: false },
    enroll: Object,
    page: Object,
    /**
     * Gezet vanaf de Specialistlink. Het token autoriseert `activities/{id}/client`
     * niet -- dat is een sessie-endpoint -- en `activities/{id}/public` antwoordt
     * alleen voor een openbare pagina (ADR-0070). Dus wordt dan de boom achter het
     * token gelezen: dezelfde `activityStructure(..., true)` zonder identiteiten
     * (ADR-0059), versmald tot de locatie van de link.
     */
    token: { type: String, default: null },
  },
  watch: {
    group_time_dialog() {
      this.$emit("close-dialog");
    },
    single_time_dialog() {
      this.$emit("close-dialog");
    },
    unavailable_dialog() {
      this.$emit("close-dialog");
    },
  },
  computed: {
    /**
     * Dezelfde regel als de Wijzigen-knop op de activiteitpagina, met de
     * botsing die de wizard zelf blokkeert: een eigen boeking van de ingelogde
     * gebruiker (functions.checkDate, zonder type -- zo vraagt StepThree het in
     * de wijzigflow). Daarbij telt het tijdslot van déze boeking nooit als
     * alternatief; bij andermans boeking dekt checkDate dat niet af. Specialist
     * is hier de toewijzing (activity_specialist), net als in de boom.
     */
    changeable() {
      const enroll = this.enroll;
      const enrollDay = toAmsterdamCalendarDate(enroll.date);

      return hasSlotToChangeTo(
        this.activity,
        (date, slot, specialist, location) =>
          functions.checkDate(date, slot.start, slot.end) ||
          (location.id == enroll.location &&
            specialist.id == enroll.activity_specialist &&
            date.getFullYear() === enrollDay.getFullYear() &&
            date.getMonth() === enrollDay.getMonth() &&
            date.getDate() === enrollDay.getDate() &&
            functions.isTimeInRange(slot.start, slot.end, enroll.start, enroll.end))
      );
    },
    cancellationPeriod() {
      return (this.page || this.mainStore.companypage)?.settings
        ?.cancellation_period;
    },
    cancellationPeriodLabel() {
      return functions.convertCancellationPeriod(this.cancellationPeriod);
    },
    /**
     * Voorbij de Afmeldtermijn wijzigt de deelnemer zijn eigen boeking niet
     * meer. Anders wijzigt hij naar een later tijdslot en meldt hij zich daar
     * alsnog af. Dezelfde regel als de API
     * (EnrollmentService::isHeldByCancellationWindow). Die geldt niet voor
     * andermans boeking, niet voor een wachtlijstplek en niet zolang er een
     * Verplaatsingsbevestiging openstaat.
     */
    heldByCancellationWindow() {
      return (
        !this.forOther &&
        !this.awaitingMoveConfirmation &&
        this.enroll.status !== "waitlisted" &&
        isPastCancellationWindow(
          this.cancellationPeriod,
          this.enroll.date,
          this.enroll.start
        )
      );
    },
    enrollDate() {
      return toAmsterdamCalendarDate(this.enroll.date).toLocaleDateString(
        this.$i18n.locale === "en" ? "en-GB" : "nl-NL",
        { day: "numeric", month: "long", year: "numeric" }
      );
    },
  },
  data: () => ({
    mainStore: useMainStore(),
    single_time_dialog: true,
    group_time_dialog: true,
    unavailable_dialog: true,
    selectedDates: [],
    activity: {},
    step: 1,
    ready: false,
    selectedDate: null,
    change_dialog: false,
  }),
  mounted() {
    this.getActivity();
  },

  methods: {
    getActivity() {
      const url = this.token
        ? `${config.url.api}/v1/link/${this.token}/activity`
        : `${config.url.api}/v1/activities/${this.enroll.activity}/client`;
      api
        .get(url)
        .then((res) => {
          this.activity = res.data.data;
          this.activity.locations.forEach((location) => {
            location.specialists.forEach((specialist) => {
              specialist.dates = specialist.dates.map((date) => {
                // Convert the ISO string to a Date object first
                const dateObject = toAmsterdamCalendarDate(date.date);

                // Then format it to 'YYYY-MM-DD'
                // This ensures compatibility with the Vuetify date picker

                return {
                  ...date, // Keep the existing properties of the date object
                  date: dateObject, // Replace the date with the formatted one
                };
              });
            });
            // this.registeractivity.location = this.enroll.location;
            // this.registeractivity.specialist = this.enroll.activity_specialist;

            this.ready = true;
          });
        })
        .catch(() => {});
    },
    onDateSelected() {
      if (this.selectedDate) {
        // Add the selected date to the registeractivity.dates array
        this.selectedDates.push(this.selectedDate);
        // Optionally, you can clear the selectedDate if needed
        this.selectedDate = null;
      }
    },

    register() {
      this.registeractivity.id = this.activity.schedule.client_activity;
      this.registeractivity.spots = this.activity.schedule.spots;
      this.registeractivity.date = this.activity.date;
      this.$emit("register-enroll", this.registeractivity);
    },
    capitalizeFirstLetter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    formatDay(epochDate) {
      // Convert epoch date to a Dutch day string (e.g., "donderdag" for Thursday)
      const dateObject = toAmsterdamCalendarDate(epochDate);
      const options = { weekday: "long" };
      return dateObject.toLocaleDateString("nl-NL", options);
    },
    formatDate(epochDate) {
      // Convert epoch date to a formatted date string (ignoring the time)
      const dateObject = new Date(epochDate);
      const year = dateObject.getFullYear();
      const month = (dateObject.getMonth() + 1).toString().padStart(2, "0");
      const day = dateObject.getDate().toString().padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    customAllowedDates(val) {
      // Check if the selected date is in the array based on the 'data' property
      const selectedDate = this.formatDate(new Date(val));
      if (this.activity.schedule.dates) {
        return (
          this.activity.schedule.dates.some(
            (dateObj) =>
              this.formatDate(toAmsterdamCalendarDate(dateObj.date)) ==
              selectedDate
          ) && !this.selectedDates.includes(val)
        );
      }
      // else if(this.activity.dates){
      //   return this.activity.dates.some((dateObj) =>
      // this.formatDate(dateObj.date * 1000) == selectedDate);

      // }
      else {
        return true;
      }
    },

    deleteitem() {},
  },
};
</script>
